import { useCallback, useEffect, useState } from "react";
import { BellRing, Check, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";

export const AccessRequestsPanel = ({ refreshKey, onDecided }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/customer/access-requests");
    setData(res.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load, refreshKey]);

  const decide = async (req, decision) => {
    setBusy(req.id);
    try {
      const { data: res } = await api.post(
        `/customer/access-requests/${req.id}/${decision}?origin_url=${encodeURIComponent(window.location.origin)}`
      );
      toast.success(
        res.status === "approved"
          ? `${res.vendor_name} can now see your credit application and trade references`
          : `Request from ${res.vendor_name} declined`
      );
      await load();
      onDecided?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  if (!data) return null;
  const approved = data.shared_with.filter((s) => s.status === "approved");
  if (!data.pending.length && !approved.length) return null;

  return (
    <div className="mb-10 space-y-6" data-testid="access-requests-panel">
      {data.pending.map((req) => (
        <div
          key={req.id}
          data-testid={`pending-request-${req.id}`}
          className="ct-rise rounded-lg border-2 border-amber-300 bg-amber-50 p-6 shadow-sm sm:p-8"
        >
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700">
                <BellRing className="h-3.5 w-3.5" /> Approval request waiting
              </div>
              <p
                className="mt-3 font-display text-lg font-extrabold tracking-tight text-slate-900"
                data-testid={`pending-request-message-${req.id}`}
              >
                {req.message}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                They matched your{" "}
                {req.matched_on === "tax_id" ? "Federal Tax ID" : "email address"} in the
                ConfirmTerms network. Approving shares your credit application and its trade
                references with them, and they stay up to date automatically — you never fill the
                form in again. Nothing is shared unless you approve.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy === req.id}
                onClick={() => decide(req, "approve")}
                data-testid={`approve-request-${req.id}`}
                className="h-11 rounded-full bg-emerald-600 px-6 font-semibold transition-colors duration-200 hover:bg-emerald-700 active:scale-95"
              >
                <Check className="mr-2 h-4 w-4" /> Approve
              </Button>
              <Button
                variant="outline"
                disabled={busy === req.id}
                onClick={() => decide(req, "decline")}
                data-testid={`decline-request-${req.id}`}
                className="h-11 rounded-full border-slate-300 px-6 font-semibold transition-colors duration-200 hover:bg-white"
              >
                <X className="mr-2 h-4 w-4" /> Decline
              </Button>
            </div>
          </div>
        </div>
      ))}

      {approved.length > 0 && (
        <div
          className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="shared-with-panel"
        >
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Share2 className="h-3.5 w-3.5 text-blue-600" /> Shared with
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Every Vendor/Supplier that has access to your credit application and trade references.
          </p>
          <div className="mt-4 divide-y divide-slate-100">
            {approved.map((s) => (
              <div
                key={s.id}
                data-testid={`shared-vendor-${s.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <span className="font-display text-base font-bold text-slate-900">
                  {s.vendor_name}
                </span>
                <span className="text-sm text-slate-500">
                  Approved {(s.decided_at || "").slice(0, 10)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
